import React, { useState, useEffect } from 'react';
import ChartPage from './components/ChartPage';
import TraditionalChart from './components/TraditionalChart';
import Navigation from './components/Navigation';
import LandingPage from './pages/LandingPage';
import BlogPage from './pages/BlogPage';
import ApiPlayground from './pages/ApiPlayground';
import SignUpPage from './pages/SignUpPage';
import SignInPage from './pages/SignInPage';
import UserDashboard from './pages/UserDashboard';
import AdminDashboard from './pages/AdminDashboard';
import CheckoutPage from './pages/CheckoutPage';
import AstroLLMPage from './pages/AstroLLMPage';
import FeatureTab from './components/FeatureTab';
import { API_BASE } from './config';

// Read user from localStorage (set by signup/signin)
function readStoredUser() {
  try {
    const raw = localStorage.getItem('astrogem_user');
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function App() {
  const [chartData, setChartData] = useState(null);
  const [birthInfo, setBirthInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeView, setActiveView] = useState('timeline'); // 'timeline' or 'traditional'
  const [page, setPage] = useState('landing'); // 'landing', 'app', 'blog', 'api', 'signup', 'signin', 'dashboard', 'admin', 'checkout'
  const [currentUser, setCurrentUser] = useState(readStoredUser);
  const [checkoutCtx, setCheckoutCtx] = useState(null); // { kind, pack?, tier?, period? }

  // Re-read user from storage when navigating (after signup/login)
  useEffect(() => {
    setCurrentUser(readStoredUser());
  }, [page]);

  // Listen for cross-component nav (e.g. AdminPanel link from UserDashboard)
  useEffect(() => {
    const handler = (e) => setPage(e.detail);
    window.addEventListener('astrogem-nav', handler);
    return () => window.removeEventListener('astrogem-nav', handler);
  }, []);

  // Google Analytics: track SPA page changes as page_view events.
  // (gtag.js is loaded in public/index.html. The initial page_view fires
  // automatically on first load; this captures subsequent state-based "pages".)
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
    window.gtag('event', 'page_view', {
      page_title: `astrogemapi.com — ${page}`,
      page_path: `/${page === 'landing' ? '' : page}`,
      page_location: window.location.href,
    });
  }, [page]);

  const handleSignOut = () => {
    localStorage.removeItem('astrogem_token');
    localStorage.removeItem('astrogem_user');
    localStorage.removeItem('astrogem_api_key');
    setCurrentUser(null);
    setPage('landing');
  };

  // Reset scroll to top when switching tabs
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [activeView]);

  // Scroll to top when chart is generated
  useEffect(() => {
    if (chartData) {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [chartData]);

  const handleChartGeneration = async (formData) => {
    setLoading(true);
    setError(null);
    try {
      // Pass JWT if signed in — backend uses it to save the birth profile
      const headers = { 'Content-Type': 'application/json' };
      const token = localStorage.getItem('astrogem_token');
      if (token) headers['Authorization'] = 'Bearer ' + token;

      const response = await fetch(`${API_BASE}/api/birth-chart`, {
        method: 'POST',
        headers,
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Server error: ${response.status}`);
      }
      const data = await response.json();
      setChartData(data);
      setBirthInfo(formData);
    } catch (err) {
      if (err.message.includes('Failed to fetch')) {
        setError(`Cannot connect to backend. Is it running on ${API_BASE}?`);
      } else {
        setError(err.message);
      }
      console.error('Chart generation error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Route to different pages
  if (page === 'landing') {
    return <LandingPage
      onGetStarted={() => setPage('app')}
      onViewApi={() => setPage('api')}
      onViewBlog={() => setPage('blog')}
      onSignup={() => setPage('signup')}
      onLogin={() => setPage('signin')}
      currentUser={currentUser}
      onOpenDashboard={() => setPage('dashboard')}
      onOpenAdmin={() => setPage('admin')}
      onOpenAstroLLM={() => setPage('astrollm')}
      onSignOut={handleSignOut}
    />;
  }
  if (page === 'blog') {
    return <BlogPage onBack={() => setPage('landing')} />;
  }
  if (page === 'api') {
    return <ApiPlayground onBack={() => setPage('landing')} />;
  }
  if (page === 'signup') {
    return <SignUpPage
      onBack={() => setPage('landing')}
      onLogin={() => setPage('signin')}
      onSuccess={() => setPage('dashboard')}
    />;
  }
  if (page === 'signin') {
    return <SignInPage
      onBack={() => setPage('landing')}
      onSignup={() => setPage('signup')}
      onSuccess={() => setPage('dashboard')}
    />;
  }
  if (page === 'dashboard') {
    return <UserDashboard
      onBack={() => setPage('app')}
      onSignOut={handleSignOut}
      onStartCheckout={(ctx) => { setCheckoutCtx(ctx); setPage('checkout'); }}
    />;
  }
  if (page === 'admin') {
    return <AdminDashboard
      onBack={() => setPage('dashboard')}
      onSignOut={handleSignOut}
    />;
  }
  if (page === 'astrollm') {
    return <AstroLLMPage
      onBack={() => setPage(currentUser ? 'dashboard' : 'landing')}
      onSignOut={handleSignOut}
    />;
  }
  if (page === 'checkout' && checkoutCtx) {
    return <CheckoutPage
      kind={checkoutCtx.kind}
      pack={checkoutCtx.pack}
      tier={checkoutCtx.tier}
      period={checkoutCtx.period}
      onBack={() => setPage('dashboard')}
      onSuccess={(result) => {
        // After payment, the webhook updates DB asynchronously.
        // Pop back to dashboard with a success banner — dashboard reloads /user/me on mount.
        setCheckoutCtx({ ...checkoutCtx, justPaid: result });
        setPage('dashboard');
      }}
    />;
  }

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-slate-900 via-purple-950 to-slate-900" style={{ minHeight: '100vh', backgroundColor: '#0f172a' }}>
      <Navigation />
      <div className="bg-slate-900/50 px-4 py-2 flex items-center justify-between flex-wrap gap-2">
        <button onClick={() => setPage('landing')} className="text-sm text-white/60 hover:text-white">← Back to Home</button>
        {currentUser ? (
          <div className="flex items-center gap-2 text-sm">
            <button onClick={() => setPage('astrollm')} className="px-2 py-0.5 bg-gradient-to-r from-purple-600/30 to-pink-600/30 border border-purple-400/40 rounded text-purple-200 text-xs font-bold">
              🪔 AstroLLM
            </button>
            {(currentUser.role === 'admin' || currentUser.tier === 'admin') && (
              <button onClick={() => setPage('admin')} className="px-2 py-0.5 bg-red-600/20 border border-red-500/40 rounded text-red-300 text-xs">
                🔐 Admin
              </button>
            )}
            <button onClick={() => setPage('dashboard')} className="px-2 py-0.5 bg-purple-600/20 border border-purple-500/40 rounded text-purple-300 text-xs">
              📊 Dashboard
            </button>
            <span className="text-xs text-white/40">{currentUser.email}</span>
            <button onClick={handleSignOut} className="text-xs text-white/50 hover:text-white">Sign out</button>
          </div>
        ) : (
          <div className="flex gap-2 text-sm">
            <button onClick={() => setPage('signin')} className="text-white/60 hover:text-white">Sign in</button>
            <button onClick={() => setPage('signup')} className="px-2 py-0.5 bg-gradient-to-r from-purple-500 to-pink-500 rounded text-xs font-bold">Sign up free</button>
          </div>
        )}
      </div>

      {/* TAB NAVIGATION - All Features */}
      {chartData && (
        <div className="bg-slate-900/80 border-b border-slate-700 sticky top-0 z-10 backdrop-blur-md">
          <div className="container mx-auto px-4 overflow-x-auto">
            <div className="flex gap-1 min-w-max">
              {[
                { id: 'timeline', icon: '📅', label: 'Life Timeline' },
                { id: 'traditional', icon: '📜', label: 'Traditional Chart' },
                { id: 'panchang', icon: '🌙', label: 'Panchang' },
                { id: 'doshas', icon: '⚖️', label: 'Doshas' },
                { id: 'yogas', icon: '✨', label: 'Yogas' },
                { id: 'shadbala', icon: '💪', label: 'Shadbala' },
                { id: 'ashtakvarga', icon: '🎯', label: 'Ashtakvarga' },
                { id: 'remedies', icon: '🪔', label: 'Remedies' }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveView(tab.id)}
                  className={`px-4 py-3 text-sm font-semibold transition border-b-2 whitespace-nowrap ${
                    activeView === tab.id
                      ? 'text-purple-400 border-purple-400'
                      : 'text-white/60 border-transparent hover:text-white hover:bg-white/5'
                  }`}
                >
                  {tab.icon} {tab.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <main className="container mx-auto px-4 pt-4 pb-12">
        {!chartData || activeView === 'timeline' ? (
          <ChartPage
            onChartGeneration={handleChartGeneration}
            chartData={chartData}
            loading={loading}
            error={error}
            onRequireSignup={() => setPage('signup')}
          />
        ) : activeView === 'traditional' ? (
          <TraditionalChart chartData={chartData} birthInfo={birthInfo} />
        ) : (
          <FeatureTab
            view={activeView}
            chartData={chartData}
            birthInfo={birthInfo}
          />
        )}
      </main>
    </div>
  );
}

export default App;
